import { useAuth } from 'react-oidc-context';
import { UnauthorisedResponse } from '~/lib/api/unauthorised-response';
import { ApiResponse } from '../api-response';
import { authedFetch } from '../authed-fetch';
import { parseResult } from '../parse-result';
import { AddPrePayResponse, GetPrePayApiBalanceResponse, GetPrePayBalanceResponse, EntitlementSource } from './types';
import { getApiPath } from './get-api-path';
import { usePaymentPath } from './use-payment-path';
import { useCallback } from 'react';
import { useTenant } from '@parcel2go/npm-p2g-app';

export interface IApiResponse<T> {
  success: boolean;
  result: T | null;
}

export function usePaymentApi() {
  const { user } = useAuth();
  const paymentPath = usePaymentPath('prepay');
  const { tenant } = useTenant();

  // Payment API returns money fields in minor units (pence); this app's toDisplayCurrency expects
  // major units, so divide by 100 here (mirrors generatePrePayLink's *100 below). freePercentage
  // is a percentage, not money - left as-is.
  const getPrePayBalance = useCallback(async (): Promise<ApiResponse<GetPrePayBalanceResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getApiPath(`prepay`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    const parsed = await parseResult<GetPrePayApiBalanceResponse>(response);
    if (!parsed.success || !parsed.result) {
      return { ...parsed, result: null };
    }

    const { balance, currency, minimumTopUp, freeThreshold, freePercentage, freePercentageEntitlement } = parsed.result;

    const showOffer = (freeThreshold ?? 0) > 0 && (freePercentage ?? 0) > 0;

    // Gated on `showOffer` too: without it a boosted rate with no threshold configured would report
    // a boost for a promo that never renders, which is a claim with no visible subject.
    const isBoostedBySubscription = showOffer && freePercentageEntitlement?.source === EntitlementSource.SUBSCRIPTION;

    return {
      ...parsed,
      result: {
        balance: balance / 100,
        currencyCode: currency,
        prePayMinimumTopUp: minimumTopUp / 100,
        freePrePayMinimumSpend: freeThreshold / 100,
        freePrePayPercentage: freePercentage,
        isBoostedBySubscription: isBoostedBySubscription,
        showOffer: showOffer
      }
    };
  }, [user?.access_token, tenant]);

  const generatePrePayLink = useCallback(
    async (amount: number, currency: string = 'GBP'): Promise<ApiResponse<AddPrePayResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getApiPath(`prepay/create`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify({ Amount: amount, CurrencyCode: currency }) },
        user?.access_token
      );
      return parseResult<AddPrePayResponse>(response).then((x) => {
        if (x.success) {
          x.result.link = `${paymentPath}?id=${x.result.orderId}&hash=${encodeURIComponent(x.result.hash)}`;
        }
        return x;
      });
    },
    [user, paymentPath, tenant]
  );

  return {
    getPrePayBalance,
    generatePrePayLink
  } as const;
}
